import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ArrowRight, Sprout, Sun, Droplets, Wind, CheckCircle, X, User, Phone, Mail, MessageSquare, Loader2, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useImages } from '../hooks/useImages';

const API = process.env.REACT_APP_BACKEND_URL;

const fadeUp = { hidden: { opacity: 0, y: 28 }, visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.12, ease: [0.25, 0.46, 0.45, 0.94] } }) };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };

const ConsultForm = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: '', contact: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch(`${API}/api/consult`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, source: 'Green Horizon' })
      });
    } catch {}
    setLoading(false);
    setSubmitted(true);
    setTimeout(() => { setSubmitted(false); setForm({ name: '', contact: '', email: '', message: '' }); onClose(); }, 2500);
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.3 }}
          className="relative w-full max-w-md z-10"
        >
          <Card className="border border-[#E8DFD3] shadow-2xl bg-white overflow-hidden">
            <div className="bg-gradient-to-r from-[#5C7A3A] to-[#4a6530] px-6 py-5 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Consult With Us</h3>
                <p className="text-xs text-white/70 mt-0.5">Green Horizon AgriTech</p>
              </div>
              <button onClick={onClose} className="p-1.5 hover:bg-white/15 rounded-lg transition-colors cursor-pointer" data-testid="close-consult-form">
                <X size={18} className="text-white" />
              </button>
            </div>
            <CardContent className="p-6">
              {submitted ? (
                <div className="text-center py-6" data-testid="consult-success">
                  <CheckCircle size={48} className="mx-auto text-[#5C7A3A] mb-3" />
                  <h4 className="text-lg font-bold text-[#2C1810] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>Thank You!</h4>
                  <p className="text-sm text-[#7a6555]">We'll get back to you within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-[#2C1810] mb-1.5 flex items-center gap-1.5">
                      <User size={13} className="text-[#7a6555]" /> Full Name
                    </label>
                    <Input
                      data-testid="consult-name"
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="Your full name"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#2C1810] mb-1.5 flex items-center gap-1.5">
                      <Phone size={13} className="text-[#7a6555]" /> Contact Number
                    </label>
                    <Input
                      data-testid="consult-contact"
                      type="tel"
                      value={form.contact}
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setForm(f => ({ ...f, contact: val }));
                      }}
                      placeholder="10-digit mobile number"
                      pattern="\d{10}"
                      maxLength={10}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#2C1810] mb-1.5 flex items-center gap-1.5">
                      <Mail size={13} className="text-[#7a6555]" /> Email
                    </label>
                    <Input
                      data-testid="consult-email"
                      type="email"
                      value={form.email}
                      onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#2C1810] mb-1.5 flex items-center gap-1.5">
                      <MessageSquare size={13} className="text-[#7a6555]" /> Message
                    </label>
                    <Textarea
                      data-testid="consult-message"
                      value={form.message}
                      onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                      placeholder="Tell us about your farming needs, land size, location..."
                      rows={3}
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#5C7A3A] hover:bg-[#4a6530] text-white font-semibold py-5 cursor-pointer"
                    data-testid="submit-consult-btn"
                  >
                    {loading ? (
                      <><Loader2 size={16} className="animate-spin mr-2" /> Sending...</>
                    ) : (
                      <><Send size={16} className="mr-2" /> Submit Inquiry</>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

const GreenHorizon = () => {
  const siteImages = useImages();
  const img = (slug, fb) => siteImages[slug] || fb;
  const [showConsult, setShowConsult] = useState(false);

  const services = [
    { icon: Sprout, title: 'Agrivoltaics', desc: 'Solar panels + farming for dual land productivity' },
    { icon: Droplets, title: 'AWG Technology', desc: 'Atmospheric water generation for irrigation' },
    { icon: Wind, title: 'Carbon Credits', desc: 'Sustainable farming with carbon offset benefits' },
    { icon: Sun, title: 'AgriTech Consulting', desc: 'Expert guidance on modern farming practices' }
  ];

  const caseStudies = [
    { title: 'Farmer Success Story - Karnataka', image: img('green-case-1', 'https://images.unsplash.com/photo-1761839257144-297ce252742e'), result: '40% increase in yield with solar integration' },
    { title: 'Water Generation Project - Telangana', image: img('green-case-2', 'https://images.unsplash.com/photo-1759509295194-e85b92b24e15'), result: '100% irrigation coverage in drought-prone areas' }
  ];

  return (
    <div className="min-h-screen" style={{ background: '#FDF8F0' }}>
      <ConsultForm open={showConsult} onClose={() => setShowConsult(false)} />

      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#5C7A3A]/8 to-[#D4A574]/5"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" initial="hidden" animate="visible" variants={stagger}>
            <div className="space-y-6">
              <motion.span variants={fadeUp} custom={0} className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-[#C4844A] bg-[#C4844A]/8 px-4 py-2 rounded-full border border-[#C4844A]/15">
                AgriTech &middot; Sustainable Farming &middot; Future-Ready
              </motion.span>
              <motion.h1 variants={fadeUp} custom={1} className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight text-[#2C1810]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Green <span className="text-[#5C7A3A]">Horizon</span>
              </motion.h1>
              <motion.p variants={fadeUp} custom={2} className="text-base text-[#5a4535] leading-relaxed max-w-lg">
                Pioneering the future of sustainable agriculture with cutting-edge technology. From agrivoltaics to atmospheric water generation, we're transforming farming for the next generation.
              </motion.p>
              <motion.div variants={fadeUp} custom={3} className="flex flex-col sm:flex-row gap-3">
                <Button size="lg" onClick={() => setShowConsult(true)} className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white font-semibold px-8 py-5 rounded-lg shadow-lg shadow-[#5C7A3A]/20 transition-all duration-300 hover:scale-105 cursor-pointer" data-testid="green-consult-btn">
                  Consult With Us <ArrowRight className="ml-2" size={18} />
                </Button>
                <Button size="lg" variant="outline" onClick={() => document.getElementById('case-studies')?.scrollIntoView({ behavior: 'smooth' })} className="border-2 border-[#5C7A3A] text-[#5C7A3A] hover:bg-[#5C7A3A] hover:text-white font-semibold px-8 py-5 rounded-lg transition-all duration-300 hover:scale-105 cursor-pointer" data-testid="green-cases-btn">
                  View Case Studies
                </Button>
              </motion.div>
            </div>
            <motion.div variants={fadeUp} custom={2}>
              <img src={img('green-hero', 'https://images.unsplash.com/photo-1761839257144-297ce252742e')} alt="Green Horizon agritech" className="w-full h-80 object-cover rounded-2xl shadow-xl shadow-[#5C7A3A]/10" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-8" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="flex items-center gap-5 justify-center mb-3">
              <div className="h-px bg-gradient-to-r from-transparent to-[#D4A574] w-16"></div>
              <Sprout size={16} className="text-[#D4A574]" />
              <div className="h-px bg-gradient-to-l from-transparent to-[#D4A574] w-16"></div>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-[#2C1810]" style={{ fontFamily: 'Playfair Display, serif' }}>Our AgriTech Solutions</motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {services.map((s, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1, duration: 0.5 }}>
                <Card className="group border border-[#E8DFD3] shadow-sm hover:shadow-lg hover:shadow-[#D4A574]/10 transition-all duration-500 hover:-translate-y-2 text-center bg-[#FDF8F0]" data-testid={`service-${idx}`}>
                  <CardContent className="p-6">
                    <div className="bg-[#5C7A3A]/10 p-3.5 rounded-2xl inline-block mb-4 group-hover:bg-[#5C7A3A]/15 transition-colors duration-300">
                      <s.icon size={28} className="text-[#5C7A3A]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#2C1810] mb-2 group-hover:text-[#5C7A3A] transition-colors duration-300">{s.title}</h3>
                    <p className="text-sm text-[#7a6555]">{s.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section id="case-studies" className="py-12" style={{ background: '#FDF8F0' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl sm:text-4xl font-bold text-[#2C1810] mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>Success Stories</motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {caseStudies.map((study, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.15, duration: 0.5 }}>
                <Card className="group border border-[#E8DFD3] shadow-md hover:shadow-xl hover:shadow-[#D4A574]/10 transition-all duration-500 hover:-translate-y-1 overflow-hidden bg-white" data-testid={`case-${idx}`}>
                  <div className="relative h-56 overflow-hidden">
                    <img src={study.image} alt={study.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2C1810]/40 to-transparent"></div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-[#2C1810] mb-3 group-hover:text-[#5C7A3A] transition-colors duration-300" style={{ fontFamily: 'Playfair Display, serif' }}>{study.title}</h3>
                    <div className="flex items-start space-x-3">
                      <CheckCircle size={20} className="text-[#5C7A3A] mt-0.5 flex-shrink-0" />
                      <p className="text-base text-[#5a4535]">{study.result}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-14 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#2C4A1A] via-[#3B5A2B] to-[#2A3F1E]"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Ready to <span className="text-[#D4A574]">Transform Your Farm?</span></h2>
            <p className="text-base text-white/75 mb-8 max-w-md mx-auto">Consult with our AgriTech experts and discover sustainable solutions</p>
            <Button size="lg" onClick={() => setShowConsult(true)} className="bg-[#D4A574] hover:bg-[#C4844A] text-white font-semibold px-8 py-5 rounded-lg shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer" data-testid="green-schedule-btn">
              Schedule Consultation <ArrowRight className="ml-2" size={18} />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GreenHorizon;
